import { type Loggable } from '@cx-web/server-utils/types';
import { type DehydratedState } from '@tanstack/react-query';

import { type ComponentTypeNames, type ScriptDescriptor } from '~/types/template';

/**
 * Union type of all component type names in the system
 */
export const componentTypeNames: ComponentTypeNames = {
  ARTICLE_FOOTER_V1: 'ARTICLE_FOOTER_V1',
  ARTICLE_HEADER_V1: 'ARTICLE_HEADER_V1',
  ARTICLES_WIDGET: 'ARTICLES_WIDGET',
  BANNERS_CAROUSEL: 'BANNERS_CAROUSEL',
  BREADCRUMBS: 'BREADCRUMBS',
  EVERGREEN_TEXT_BLOCK: 'EVERGREEN_TEXT_BLOCK',
  GRID_GROUP: 'GRID_GROUP',
  ICON_LIST: 'ICON_LIST',
  INLINE_HTML: 'INLINE_HTML',
  LANDING_PAGE_FOOTER: 'LANDING_PAGE_FOOTER',
  LANDING_PAGE_HEADER: 'LANDING_PAGE_HEADER',
  MATCHES_COUPON: 'MATCHES_COUPON',
  MOST_POPULAR_BETS: 'MOST_POPULAR_BETS',
  OC_CONNECT_WIDGET: 'OC_CONNECT_WIDGET',
  OFFERS_CAROUSEL: 'OFFERS_CAROUSEL',
  OFFERS_GROUPED: 'OFFERS_GROUPED',
  OFFERS_PAGE: 'OFFERS_PAGE',
  OFFERS_SLIM: 'OFFERS_SLIM',
  ONBOARDING_WIDGET: 'ONBOARDING_WIDGET',
  OUTRIGHTS_COUPON: 'OUTRIGHTS_COUPON',
  PARENT_LINK_HEADER: 'PARENT_LINK_HEADER',
  PREMIUM_SIGNUP: 'PREMIUM_SIGNUP',
  QUICK_LINKS_LIST: 'QUICK_LINKS_LIST',
  REVIEWS_SECTION: 'REVIEWS_SECTION',
  SECTION_FAQ: 'SECTION_FAQ',
  SUBEVENT_HEADER: 'SUBEVENT_HEADER',
  TABLE_OF_CONTENTS: 'TABLE_OF_CONTENTS',
};

/**
 * Server loader function result for CX Module components (from @cx-modules packages)
 * These components use the dehydratedState pattern for React Query hydration
 */
export type CxModuleComponentServerLoaderFunctionResult = {
  componentArgs?: any;
  dehydratedState: DehydratedState;
  paginationMeta?: any;
  /**
   * The module's prebuilt stylesheet, linked into `<head>`. Optional since NGF-6661: only the modules
   * still consumed from the registry ship a `dist/style.css` to point at. The ones embedded under
   * `modules/` are compiled from source by this app's own Vite pipeline, so their CSS is already in
   * the bundle the page loads and there is nothing to link.
   */
  ssrConfig?: {
    cssBundle: string;
  };
};

/**
 * Server loader function result for non-CX Module components (custom components in this app)
 * These components use componentArgs directly without React Query hydration
 */
export type NonCxComponentServerLoaderFunctionResult = {
  componentArgs: any;
  componentScripts?: ScriptDescriptor[];
  componentStyles?: string[];
  isNotCxModule: true;
};

/**
 * Union type of all possible server loader function results
 * null indicates no server-side data loading is required for the component
 */
export type ServerLoaderFunctionResult =
  CxModuleComponentServerLoaderFunctionResult | NonCxComponentServerLoaderFunctionResult | null;

/**
 * Server component map type
 * Contains server-side loader functions for each component type
 * Scripts are metadata descriptors (not code) that tell the client which external scripts to load
 */
export type ServerComponentMap = {
  [K in keyof ComponentTypeNames]?: {
    name: K;
    scripts?: ScriptDescriptor[];
    serverLoaderFunction: (this: Loggable, props: any) => Promise<ServerLoaderFunctionResult>;
  };
};

/**
 * Client component map type
 * Contains React components and optional scripts for each component type
 */
export type ClientComponentMap = {
  [K in keyof ComponentTypeNames]?: {
    Component: React.ComponentType<any>;
    name: K;
    scripts?: ScriptDescriptor[];
  };
};
